import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Dialog, Menu, MenuItem } from 'primeng/primeng';
import {IStrategy} from '../../models/IStrategy';
import { StrategiesService } from '../services/strategies.service';
import { StrategyDetailsComponent  } from './strategy-details/strategy-details.component';
import { AppBannerMsgComponent } from '../../app-banner-msg/app-banner-msg.component';
import { StrategiesPermissionsService } from '../services/strategies-permissions.service';
import { StrategyMapDownloadService, DownloadConfiguration } from '../strategy-map-download/strategy-map-download.service';
import { PriorityService } from '../services/priority.service';
import { pluck, finalize } from 'rxjs/operators';
import {StrtDashboardSortColumn} from '../../shared/models/api/StrtDashboardSortColumn';
import {DefaultDashboardRequest, StrtDashboardRequest} from '../../shared/models/api/StrtDashboardRequest';
import {AppLoadingService} from '../../app-loading-panel/app-loading.service';
import {BehaviorSubject, Observable, Subscription} from 'rxjs/index';
import {LazyLoadEvent, SortMeta} from 'primeng/api';
import {MenuItemTypeEnum} from '../../shared/classes/menu-item-type-enum';
import {MyMenuItem} from '../../shared/classes/MyMenuItem.interface';
import {SharedSortFilterMenuComponent} from '../../shared/components/shared-sort-filter-menu/shared-sort-filter-menu.component';
import * as _ from 'lodash';
import {Table} from 'primeng/table';
import {CustomerService} from '../../auth/services/customer.service';
import {ICustomer} from '../../auth/models/ICustomer';
import {CopyPlanFormComponent} from '../../shared/components/copy-plan-form/copy-plan-form.component';
import {CGSearchComponent} from '../../shared/components/cg-search/cg-search.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild(AppBannerMsgComponent) private banner: AppBannerMsgComponent;
  @ViewChild(StrategyDetailsComponent) private strategyDetailsComponent: StrategyDetailsComponent;
  @ViewChild('confirmDialog') private confirmDialog: Dialog;
  @ViewChild('copyPlanModal') copyPlanDialog: Dialog;
  @ViewChild('downloadDialog') private downloadDialog: Dialog;
  @ViewChild('contextMenu') private contextMenu: Menu;
  @ViewChild('statusMenu') statusMenu: SharedSortFilterMenuComponent;
  @ViewChild('strategiesDashboardTable') table: Table;
  @ViewChild('copyPlanForm') copyPlanForm: CopyPlanFormComponent;
  @ViewChild('cgSearch') searchBar: CGSearchComponent;


  private _strategies: IStrategy[] = [];
  selectedStrategy: IStrategy = {strategyName: '', startDate: new Date(), endDate: new Date()};
  institutionId: number;

  private openStrategyMenuItem = {label: 'Open', icon: 'fa fa-folder-open-o', command: () => this.openStrategy()};
  private editStrategyMenuItem = {label: 'Edit', icon: 'fa fa-pencil', command: () => this.editStrategy()};
  private downloadStrategyMenuItem = {label: 'Download', icon: 'fa fa-download', command: () => this.downloadStrategy()};
  private deleteStrategyMenuItem = {label: 'Delete', icon: 'fa fa-trash', command: () => this.deleteStrategy()};
  private copyStrategyMenuItem = {label: 'Copy', icon: 'fa fa-copy', command: () => this.onCopyStrategyClick()};
  private searchWordsBS: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  searchWords$ = this.searchWordsBS.asObservable();
  private _menuItems: MenuItem[] = [
    this.openStrategyMenuItem,
    this.editStrategyMenuItem,
    this.downloadStrategyMenuItem,
    this.deleteStrategyMenuItem,
    this.copyStrategyMenuItem
  ];
  readonly ASCENDINGAZ = 'ASCENDINGAZ';
  readonly DESCENDINGZA = 'DESCENDINGZA';
  sortedColumn = 'statusFilterValue';
  sortDirection: 'ASCENDINGAZ' | 'DESCENDINGZA' = this.ASCENDINGAZ;
  columnHeadersClicked = false;
  showLeftNav = false;
  isStrategyDetailsVisible = false;
  confirmDownloadSysAggReportVisible = false;

  appHeaderLeftAction = {
    icon: 'icon-menu',
    action: () => {
      this.showLeftNav = true;
    }
  };
  private _lastSearchedValue = '';
  private columnMap = {
    'strategyName' : 'strategyName',
    'Name': 'strategyName',
    'institutionName' : 'institutionName',
    'creatorFullName' : 'creatorFullName',
    'lastModifiedDate': 'lastModifiedDate',
    'startDate': 'startDate',
    'endDate': 'endDate',
    'status': 'status'
  };

  statusMenuOptions: MyMenuItem[] = [
    {
      label: 'Sort',
      icon: 'eleot-icon icon-arrow-up',
      command: () => {
        this.executeStatusSort(this.ASCENDINGAZ);
      },
      type: MenuItemTypeEnum.Sort
    },
    {
      label: 'Sort',
      icon: 'eleot-icon icon-arrow-down',
      command: () => {
        this.executeStatusSort(this.DESCENDINGZA);
      },
      type: MenuItemTypeEnum.Sort
    },
    {
      type: MenuItemTypeEnum.Separator,
      disabled: true
    },
    {
      label: 'Started',
      icon: '',
      command: (isChecked: boolean) => {
        this.updateFilter('STARTED', isChecked);
      },
      type: MenuItemTypeEnum.Filter
    },
    {
      label: 'In Progress',
      icon: '',
      command: (isChecked: boolean) => {
        this.updateFilter('INPROGRESS', isChecked);
      },
      type: MenuItemTypeEnum.Filter
    },
    {
      label: 'Completed',
      icon: '',
      command: (isChecked: boolean) => {
        this.updateFilter('COMPLETED', isChecked);
      },
      type: MenuItemTypeEnum.Filter
    }
  ];

  refetchTimer;

  multiSortMeta: SortMeta[];
  pageIncludeHierarchy = DefaultDashboardRequest.includeHierarchy;
  pageOffset = DefaultDashboardRequest.offset;
  pageSize = DefaultDashboardRequest.pageSize;
  pageSortColumns: StrtDashboardSortColumn[] = DefaultDashboardRequest.sortColumns;
  pageFilters: string[] = DefaultDashboardRequest.filters;
  pageSearchTerm: string|null = DefaultDashboardRequest.searchTerm;
  isLoading$: Observable<boolean>;
  showHierarchyToggle = false;
  totalCount = -1;
  private _subscriptions: Subscription[] = [];

  constructor(private route: ActivatedRoute,
              private router: Router,
              private loadingService: AppLoadingService,
              private strategiesService: StrategiesService,
              private customerService: CustomerService,
              private strategiesPermissionsService: StrategiesPermissionsService,
              private strategyMapDownloadService: StrategyMapDownloadService,
              private priorityService: PriorityService) { }

  ngOnInit() {
    this.showHierarchyToggle = false;
    this.isLoading$ = this.loadingService.isLoading$;
    this.loadingService.setLoading();
    this.route.params.pipe(pluck('institutionId'))
      .subscribe(institutionId => this.institutionId = Number(institutionId));
    this._subscriptions.push(this.customerService.selectedInstitutionMeta$.subscribe((customer: ICustomer) => {
      this.setupHierarchyToggle(customer);
    }));
    this.resetFilters();
    this.getTotalCount();
    this.customerService.childInstitutions = this.route.snapshot.data['childInstitutions'];
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      for (const subscription of this._subscriptions) {
        subscription.unsubscribe();
      }
    }
  }

  getTotalCount() {
    this.strategiesService.totalCount(this.institutionId, this.pageIncludeHierarchy).subscribe((count: number) => {
      this.totalCount = count;
    }, (err) => {
      this.totalCount = -1;
    });
  }

  setupHierarchyToggle(institution) {
    const role = this.strategiesPermissionsService.userInstitutionRole;
    this.showHierarchyToggle = institution ? ((role === 'ROLE_ADMIN' || role === 'ROLE_SUPER_ADMIN') && institution.hasChildren) : false;
  }

  private resetFilters() {
    this.pageFilters = [];
  }

  private updateFilter(filterVal: string, checked: boolean) {
    if (checked) {
      this.pageFilters.push(filterVal);
    } else {
      this.pageFilters = this.pageFilters.filter(
        item => item !== filterVal
      );
    }
    const whiteSpaceRegExp: RegExp = /\s+/g;
    const valArray: string[] = (this._lastSearchedValue || '')
      .split(whiteSpaceRegExp)
      .filter((term: string) => term !== '');
    console.log(`Execute search here -> This is in the update filter handler\n
    Filter Val: ${filterVal}\n
    Checked: ${checked}\n
    Val Array: ${valArray}\n
    Current Filters: ${this.pageFilters}`);
    this.pageOffset = 0;
    this.refetchCurrentData(true);
  }

  toggleStatusMenu = event => {
    this.statusMenu.toggleShowMenu(event);
  }

  onCopyPlanSuccess(strategyName: string) {
    this.copyPlanDialog.visible = false;
    this.copyPlanForm.reset();
    this.pageOffset = 0;
    const successMessage = `Your plan: <strong>${strategyName}</strong> has been copied.`;
    this.banner.addSuccessMessage(successMessage);
    this.refetchCurrentData(true);
  }

  executeStatusSort(sortDirection: 'ASCENDINGAZ' | 'DESCENDINGZA') {
    this.table.sortOrder = 0;
    this.table.sortField = '';
    this.table.reset();
    this.sortedColumn = 'statusFilterValue';
    console.log(`Executing Status Sort With Direction: ${sortDirection}`);
    this.sortDirection = sortDirection;
    const column: StrtDashboardSortColumn = {
      columnName: 'status',
      direction: sortDirection === 'ASCENDINGAZ' ? 'asc' : 'desc'
    };
    this.pageSortColumns = [column, ...DefaultDashboardRequest.sortColumns];
    this.multiSortMeta = null;
    this.pageOffset = 0;
    this.refetchCurrentData(true);
  }
  /**
   * End code for sort / filter popover
   */

  searchValueChanged(searchValue: string) {
    this.pageSearchTerm = searchValue;
    this._lastSearchedValue = searchValue;
    this.searchWordsBS.next(searchValue ? searchValue.split(/\s/g) : []);
    this.pageOffset = 0;
    this.refetchCurrentData(true);
  }

  searchBarExpanded() {
  }

  searchBarCollapsed() {
  }

  get menuItems() {
    return this._menuItems;
  }

  get strategies() {
    return this._strategies;
  }

  get strategiesLength() {
    return this._strategies.length;
  }

  canAddStrategy() {
    return this.strategiesPermissionsService.userInstitutionPermissions.canAdd;
  }

  onToggleContextMenu($event, strategy) {
    this.contextMenu.toggle($event);
    this.selectedStrategy = strategy;
    this.setActionList();
  }

  onStrategyRowDblClick(event) {
    this.selectedStrategy = event.data;
    this.openStrategy();
  }

  onCreateStrategy() {
    this.isStrategyDetailsVisible = true;
  }

  onToggleAssociatedInstitutions(event) {
    console.log(`On Toggle Associated Institutions: `, event);
    this.pageIncludeHierarchy = event.checked;
    this.pageOffset = 0;
    this.refetchCurrentData(true);
    this.getTotalCount();
  }

  getCurrentPageRequest(): StrtDashboardRequest {
    return {
      sortColumns: this.pageSortColumns,
      searchTerm: this.pageSearchTerm,
      pageSize: this.pageSize,
      offset: this.pageOffset,
      includeHierarchy: this.pageIncludeHierarchy,
      filters: this.pageFilters,
    };
  }

  getDownloadSystemAggRequest(): StrtDashboardRequest {
    return {
      sortColumns: this.pageSortColumns,
      searchTerm: this.pageSearchTerm,
      pageSize: this._strategies.length, // page size is entire length of visible strategies
      offset: 0, // offset will always be zero since we are requesting the entire visible data set in one go
      includeHierarchy: this.pageIncludeHierarchy,
      filters: this.pageFilters,
    };
  }


  cycleRefetchTimer() {
    if (this.refetchTimer) {
      clearTimeout(this.refetchTimer);
    }
    this.refetchTimer = setTimeout(() => {
      this.loadingService.setLoading();
    }, 1);
  }

  scrollTableToTop() {
    document.querySelector('.ui-table-scrollable-body').scrollTop = 0;
  }

  refetchCurrentData(scrollToTop: boolean = false) {
    this.cycleRefetchTimer();
    const dashboardPageRequest: StrtDashboardRequest = this.getCurrentPageRequest();

    const errorHandler = (err) => {
      clearTimeout(this.refetchTimer);
      this.loadingService.clearLoading();
    };
    const successHandler = (strategies) => {
      clearTimeout(this.refetchTimer);
      setTimeout(() => {
        if (scrollToTop) {
          this.scrollTableToTop();
        }
        this._strategies = strategies;
        this.pageOffset = this._strategies.length;
        this.loadingService.clearLoading();
      });
    };

    if (dashboardPageRequest.searchTerm) {
      this.strategiesService.search(this.institutionId, dashboardPageRequest).subscribe(successHandler, errorHandler);
    } else {
      this.strategiesService.list(this.institutionId, dashboardPageRequest).subscribe(successHandler, errorHandler);
    }

  }

  fetchNextData() {
    this.cycleRefetchTimer();
    const dashboardPageRequest: StrtDashboardRequest = this.getCurrentPageRequest();

    const errorHandler = (err) => {
      clearTimeout(this.refetchTimer);
      this.loadingService.clearLoading();
    };
    const successHandler = (strategies) => {
      clearTimeout(this.refetchTimer);
      this._strategies = [...this._strategies, ...strategies];
      this.pageOffset = this._strategies.length;
      this.loadingService.clearLoading();
    };
    if (dashboardPageRequest.searchTerm) {
      this.strategiesService.search(this.institutionId, dashboardPageRequest).subscribe(successHandler, errorHandler);
    } else {
      this.strategiesService.list(this.institutionId, dashboardPageRequest).subscribe(successHandler, errorHandler);
    }
  }

  get totalRecordCount() {
    return this._strategies.length < this.totalCount ?
      this._strategies.length + 1 :
      (this.totalCount >= 0 ? this.totalCount : 0);
  }

  hasFiltersOrSearch() {
    return this.pageFilters.length > 0 || (this.pageSearchTerm && this.pageSearchTerm.trim().length > 0);
  }

  loadDataOnScroll(event: LazyLoadEvent) {
    this.sortedColumn = event.multiSortMeta && event.multiSortMeta.length > 0 ? event.multiSortMeta[0].field : '';
    let forceRefresh = false;
    if (this.sortedColumn !== '') {
      const column: StrtDashboardSortColumn = {
        columnName: this.columnMap[event.multiSortMeta[0].field],
        direction: event.multiSortMeta[0].order === 1 ? 'desc' : 'asc'
      };
      if (column.columnName === DefaultDashboardRequest.sortColumns[0].columnName) {
        // forceRefresh = this.pageSortColumns !== DefaultDashboardRequest.sortColumns;
        const newColumns = [column];
        forceRefresh = !this.isArrayEqual(this.pageSortColumns, newColumns);
        this.pageSortColumns = newColumns;
      } else {
        const newSortColumns = [column, ...DefaultDashboardRequest.sortColumns];
        // forceRefresh = this.pageSortColumns !== newSortColumns;
        forceRefresh = !this.isArrayEqual(this.pageSortColumns, newSortColumns);
        this.pageSortColumns = newSortColumns;
      }
      console.log(`Load Data On Scroll - Sorted Column: `, column);
    }
    console.log(`Load Data On Scroll - Force Refresh: ${forceRefresh ? 'TRUE' : 'FALSE'}`);
    if (this._strategies.length > 0 && !forceRefresh) {
      this.fetchNextData();
    } else {
      if (forceRefresh) {
        this.pageOffset = 0;
      }
      this.refetchCurrentData(true);
    }
  }

  isArrayEqual(x, y) {
    return _(x).differenceWith(y, _.isEqual).isEmpty();
  }

  onConfirmDialogAccept() {
    const strategy = this.selectedStrategy;
    this.strategiesService.deleteStrategy(strategy.strategyId)
      .pipe(finalize(() => this.confirmDialog.visible = false))
      .subscribe(() => {
        this.banner.addSuccessMessage(`Your plan: <strong>${strategy.strategyName}</strong> has been deleted.`);
        this.pageOffset = 0;
        this.refetchCurrentData();
        }, error => this.banner.addErrorMessage(error.message));
  }

  onStrategyCreated(strategy: IStrategy) {
    let successMessage = `Your plan: <strong>${strategy.strategyName}</strong> has been created`;
    if (strategy.targetStartDate) {
      const startDate = `${strategy.targetStartDate[1]}/${strategy.targetStartDate[2]}/${strategy.targetStartDate[0]}`;
      successMessage += ` for <strong>${startDate}</strong>`;
    }
    if (strategy.targetEndDate) {
      const endDate = `${strategy.targetEndDate[1]}/${strategy.targetEndDate[2]}/${strategy.targetEndDate[0]}`;
      successMessage += ` to <strong>${endDate}</strong>`;
    }
    successMessage += '.'; // end with a period
    this.isStrategyDetailsVisible = false;
    this.banner.addSuccessMessage(successMessage);
    this.pageOffset = 0;
    this.refetchCurrentData(true);
  }

  onStrategyEdited(editObject: {edited: IStrategy, pristine: IStrategy}) {
    const strategy: IStrategy = editObject.edited;
    const oldStrategyName: string = editObject.pristine.strategyName;
    let successMessage =
      `Your plan: <strong>${oldStrategyName}</strong> has been changed to <strong>${strategy.strategyName}</strong>`;
    if (strategy.targetStartDate) {
      const startDate = `${strategy.targetStartDate[1]}/${strategy.targetStartDate[2]}/${strategy.targetStartDate[0]}`;
      successMessage += ` for <strong>${startDate}</strong>`;
    }
    if (strategy.targetEndDate) {
      const endDate = `${strategy.targetEndDate[1]}/${strategy.targetEndDate[2]}/${strategy.targetEndDate[0]}`;
      successMessage += ` to <strong>${endDate}</strong>`;
    }
    successMessage += '.'; // end with a period
    this.isStrategyDetailsVisible = false;
    this.banner.addSuccessMessage(successMessage);
    this.pageOffset = 0;
    this.refetchCurrentData(true);
  }

  onStrategyError(error: Error) {
    this.isStrategyDetailsVisible = false;
    this.banner.addErrorMessage(error.message);
  }

  downloadSystemAggReport() {
    this.loadingService.setLoading();
    this.strategiesService.downloadSystemAggReport(this.getDownloadSystemAggRequest(), this.institutionId).subscribe((result: any) => {
      this.searchBar.collapseSearch();
      this.loadingService.clearLoading();
      this.confirmDownloadSysAggReportVisible = true;
    }, (err) => {
      this.loadingService.clearLoading();
      console.log(`Error requesting system agg download: `, err);
    });
  }

  private setActionList() {
    this._menuItems = [];
    const permissions = this.strategiesPermissionsService.userInstitutionPermissions;
    if (permissions.canView) {
      this._menuItems.push(this.openStrategyMenuItem);
    }
    if (permissions.canEditNameDates) {
      this._menuItems.push(this.editStrategyMenuItem);
    }
    if (permissions.canDownload) {
      this._menuItems.push(this.downloadStrategyMenuItem);
    }
    if (permissions.canDelete) {
      this._menuItems.push(this.deleteStrategyMenuItem);
    }
    if (permissions.canCopy) {
      this._menuItems.push(this.copyStrategyMenuItem);
    }
  }

  private openStrategy() {
    this.router.navigate(['/strategies', this.selectedStrategy.strategyId]);
  }

  private editStrategy() {
    this.isStrategyDetailsVisible = true;
    this.strategyDetailsComponent.onEditStrategy(this.selectedStrategy);
  }

  private downloadStrategy() {
    this.downloadDialog.visible = true;
    const config = this.createDownloadConfig();

    this.priorityService.getPrioritiesOutcomes(this.selectedStrategy.strategyId).subscribe(
      (priorities) => {
        config.priorities = priorities;
        config.formConfiguration.isPlanEmpty = priorities.length === 0;
        this.downloadDialog.visible = true;
        this.strategyMapDownloadService.setVisible(config);
      },
      (error) => {
        this.banner.addErrorMessage(error.message);
      }
    );
  }

  private createDownloadConfig(): DownloadConfiguration {
    const config = new DownloadConfiguration();
    config.strategyId = this.selectedStrategy.strategyId;
    config.strategyName = this.selectedStrategy.strategyName;
    config.formConfiguration.allCheckbox = true;
    config.formConfiguration.submitButton = 'Download';
    return config;
  }

  private deleteStrategy() {
    this.confirmDialog.visible = true;
  }

  private onCopyStrategyClick() {
    this.copyPlanForm.loadStrategy();
  }

  onCopyStrategyLoaded() {
    this.copyPlanDialog.visible = true;
  }

  closeStatusMenu(event) {
    if (this.statusMenu.showMenu) {
      this.statusMenu.toggleShowMenu(event);
    }
  }
}
